import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useApiKeyList, useDeleteApiKey } from '../api';
import type { ApiKeyRow } from '../types';

export function ApiKeyListPage() {
  const navigate = useNavigate();
  const query = useApiKeyList();
  const del = useDeleteApiKey();
  const rows = query.data ?? [];
  const [pendingDelete, setPendingDelete] = useState<ApiKeyRow | null>(null);

  const columns = useMemo<ColumnDef<ApiKeyRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'apikey',
        header: 'API key',
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs">{row.original.apikey}</span>
        ),
      },
      { accessorKey: 'ip', header: 'IP / Host', size: 200 },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>API key #{row.original.id}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/update-api-key/${row.original.id}`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate],
  );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'API key deleted');
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the API key.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Keys"
        description="Keys your applications use to authenticate against the API."
        actions={
          <Button asChild>
            <Link to="/create-api-key">
              <Plus className="h-4 w-4" />
              Add API key
            </Link>
          </Button>
        }
      />

      <DataTable<ApiKeyRow, unknown>
        tableId="api-keys:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search API keys…"
        emptyTitle="No API keys yet"
        emptyDescription="Create an API key to start authenticating against the API."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="api-keys.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            API key{' '}
            <span className="font-mono text-xs text-foreground">
              {pendingDelete?.apikey.slice(0, 16)}…
            </span>{' '}
            will be permanently deleted. Anything authenticating with this key will stop working.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
